import { BrowserModule } from '@angular/platform-browser';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from './services/global-error-handler';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
    FooterModule,
    MenuModule,
    RouteChangeService,
    SharedModule,
} from 'merlin-library';
import { HttpTokenInterceptor } from 'merlin-core';
import { helpDialog, ShellComponent } from './shell/shell.component';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { LoaderInterceptor } from 'src/core/interceptors/global-loader.interceptor';
import { TranslateLoader, TranslateModule, TranslateFakeLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OidcModule } from './oidc-base/oidc.module';
import { OAuthModule } from 'angular-oauth2-oidc';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { SessionTimeoutComponent } from './session-timeout/session-timeout.component';
import { SessionTimeoutService } from './session-timeout/session-timeout.service';
import { HighchartsChartModule } from 'highcharts-angular';
import { HttpErrorInterceptor } from 'src/core/interceptors/http-error.interceptor';
import { LogLevel } from '@azure/msal-common';
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    PublicClientApplication,
} from '@azure/msal-browser';
import {
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalRedirectComponent,
    MsalService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { ImpersonateComponent } from './impersonate/impersonate.component';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorIntl } from 'projects/clinician/src/app/shared/custom-mat-paginator-intl';
import { SignoutComponent } from './signout/signout.component';
import { TermsConditionsComponent } from './terms-conditions/terms-conditions.component';
import { OneTimeWizardComponent } from './one-time-wizard/one-time-wizard.component';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { UnAuthorizedComponent } from './unauthorized/unauthorized.component';
import { LastActiveService } from './last-active/last-active.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { OneTimeWizardSuccessComponent } from './one-time-wizard-success/one-time-wizard-success.component';
import { LegacyComponent } from './legacy/legacy.component';
import { RedirectSsoTechsupportComponent } from './redirect-sso-techsupport/redirect-sso-techsupport.component';


const isIE =
    window.navigator.userAgent.indexOf('MSIE ') > -1 ||
    window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
    // console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    const knownAuthorities = [environment.knownAuthorities];
    try {
        const impersonateHost = new URL(environment.impersonateAuthority).hostname;
        if (impersonateHost && !knownAuthorities.includes(impersonateHost)) {
            knownAuthorities.push(impersonateHost);
        }
    } catch (e) { /* impersonateAuthority not configured */ }
    return new PublicClientApplication({
        auth: {
            clientId: environment.client_id,
            authority: environment.authority,
            redirectUri: environment.redirectUri,
            knownAuthorities: knownAuthorities,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
            storeAuthStateInCookie: isIE, // set to true for IE 11
        },
        system: {
            loggerOptions: {
                loggerCallback,
                logLevel: LogLevel.Info,
                piiLoggingEnabled: false,
            },
        },
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(
        environment.scope,
        environment.scopePermissions.split(' ')
    );

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [environment.openid, environment.profile],
        },
    };
}

// this includes the core NgIdleModule but includes keepalive providers for easy wireup
@NgModule({ declarations: [
        AppComponent,
        ShellComponent,
        SessionTimeoutComponent,
        helpDialog,
        ImpersonateComponent,
        SignoutComponent,
        TermsConditionsComponent,
        UnAuthorizedComponent,
        OneTimeWizardComponent,
        OneTimeWizardSuccessComponent,
        LegacyComponent,
        RedirectSsoTechsupportComponent,
    ],
    exports: [SharedModule],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [ReactiveFormsModule,
        BrowserModule,
        AppRoutingModule,
        MatFormFieldModule,
        MatInputModule,
        OidcModule,
        MenuModule,
        OAuthModule.forRoot(),
        FooterModule,
        SharedModule.forRoot(environment),
        NgIdleKeepaliveModule.forRoot(),
        BrowserAnimationsModule,
        HighchartsChartModule,
        PdfJsViewerModule,
        TranslateModule.forRoot({}),
        MsalModule], providers: [
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptor,   // Must be BEFORE HttpTokenInterceptor so it sees cached responses too
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpTokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        SessionTimeoutService,
        RouteChangeService,
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: MatPaginatorIntl,
            useClass: CustomMatPaginatorIntl,
        },
        provideAppInitializer(() => {
        const initializerFn = ((lastActiveService: LastActiveService) => () => lastActiveService.setUp())(inject(LastActiveService));
        return initializerFn();
      }),
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule { }
